import { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Upload, Download, X, ImagePlus } from "lucide-react";
import { api, rupee, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Pagination, usePageSlice } from "@/components/Pagination";
import { toast } from "sonner";

const PER_PAGE = 10;

const empty = {
  name: "", code: "", category: "", audience: "", description: "", colors: [], fabric: "",
  pattern: "", length: "", neck: "", sleeve: "", padding: "", addon: "", net_quantity: "",
  generic_name: "", country_of_origin: "India", brand: "Gujarish BODYCRUSH",
  sizes: [], mrp: "", images: [], size_chart_image: "", visible: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [attrs, setAttrs] = useState({ category: [], audience: [], size: [], color: [], fabric: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);

  const load = () => api.get("/products", { params: { include_hidden: true } }).then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/attributes").then((r) => setAttrs(r.data)); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...empty, ...p, mrp: p.mrp || "", colors: p.colors || [], sizes: p.sizes || [], images: p.images || [] });
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = { ...form, mrp: form.mrp ? Number(form.mrp) : null, sizes: form.sizes.map((s) => ({ ...s, price: Number(s.price), stock: Number(s.stock) })) };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/products/${id}`); toast.success("Deleted"); load(); };
  const toggleVis = async (id) => { await api.patch(`/products/${id}/visibility`); load(); };

  const { paged, totalPages } = usePageSlice(products, page, PER_PAGE);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold">Products</h1>
        <div className="flex gap-2">
          <BulkUpload onDone={load} />
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-semibold" data-testid="add-product-btn"><Plus className="w-4 h-4" /> Add Product</button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr>
                <th className="text-left p-3 font-medium">Product</th>
                <th className="text-left p-3 font-medium hidden sm:table-cell">Code</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Category</th>
                <th className="text-left p-3 font-medium">Price</th>
                <th className="text-left p-3 font-medium">Stock</th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`admin-product-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img src={fileUrl(p.images?.[0]) || "https://via.placeholder.com/60"} alt="" className="w-10 h-12 rounded object-cover bg-secondary" />
                      <span className="font-medium">{p.name}{!p.visible && <span className="text-xs text-muted-foreground ml-2">(hidden)</span>}</span>
                    </div>
                  </td>
                  <td className="p-3 font-mono text-xs hidden sm:table-cell">{p.code}</td>
                  <td className="p-3 hidden md:table-cell">{p.category}</td>
                  <td className="p-3 font-mono">{rupee(p.min_price)}</td>
                  <td className="p-3 font-mono">{p.sizes?.reduce((s, x) => s + (x.stock || 0), 0)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleVis(p.id)} className="p-2 hover:text-primary" data-testid={`toggle-vis-${p.id}`}>{p.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                      <button onClick={() => openEdit(p)} className="p-2 hover:text-primary" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(p.id)} className="p-2 hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {products.length === 0 && <p className="p-8 text-center text-muted-foreground">No products yet.</p>}
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      <ProductDialog open={open} setOpen={setOpen} form={form} setForm={setForm} attrs={attrs} save={save} editId={editId} />
    </div>
  );
}

function BulkUpload({ onDone }) {
  const ref = useRef();
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);

  const downloadTemplate = async () => {
    const res = await api.get("/products/bulk/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "bodycrush_products_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/products/bulk/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); setOpen(true); onDone();
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  return (
    <>
      <button onClick={downloadTemplate} className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-2 text-sm font-medium" data-testid="download-template-btn"><Download className="w-4 h-4" /> Template</button>
      <button onClick={() => ref.current.click()} className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-2 text-sm font-medium" data-testid="bulk-upload-btn"><Upload className="w-4 h-4" /> Excel/CSV</button>
      <input ref={ref} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} data-testid="bulk-upload-input" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="admin bg-card border-border text-foreground">
          <DialogHeader><DialogTitle>Import Result</DialogTitle></DialogHeader>
          {result && (
            <div className="text-sm space-y-2">
              <p>Created: <b>{result.created}</b> · Updated: <b>{result.updated}</b></p>
              {result.errors?.length > 0 && (
                <div className="max-h-48 overflow-y-auto text-destructive text-xs space-y-1">
                  {result.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ImageUploader({ label, value, onChange, multiple }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const imgs = multiple ? (value || []) : (value ? [value] : []);

  const handle = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onChange(multiple ? [...(value || []), ...urls] : urls[0]);
    } catch { toast.error("Upload failed"); } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="flex flex-wrap gap-2 mt-1.5">
        {imgs.map((im, i) => (
          <div key={i} className="relative w-16 h-20 rounded-lg overflow-hidden border border-border">
            <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange(multiple ? value.filter((_, idx) => idx !== i) : "")} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X className="w-3 h-3 text-white" /></button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current.click()} disabled={busy} className="w-16 h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary" data-testid={`upload-${label}`}>
          {busy ? "…" : <ImagePlus className="w-5 h-5" />}
        </button>
        <input ref={ref} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={handle} />
      </div>
    </div>
  );
}

function ProductDialog({ open, setOpen, form, setForm, attrs, save, editId }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleArr = (k, v) => setForm({ ...form, [k]: form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v] });

  const toggleSize = (size) => {
    const exists = form.sizes.find((s) => s.size === size);
    setForm({ ...form, sizes: exists ? form.sizes.filter((s) => s.size !== size) : [...form.sizes, { size, price: form.mrp || 0, stock: 0 }] });
  };
  const setSizeField = (size, field, val) => setForm({ ...form, sizes: form.sizes.map((s) => s.size === size ? { ...s, [field]: val } : s) });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="admin bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
        <Tabs defaultValue="basic">
          <TabsList className="bg-secondary">
            <TabsTrigger value="basic" data-testid="tab-basic">Basic</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Sizes & Pricing</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Images</TabsTrigger>
            <TabsTrigger value="specs" data-testid="tab-specs">Specs</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 pt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Name*" value={form.name} onChange={set("name")} testid="pf-name" />
              <F label="Code / SKU" value={form.code} onChange={set("code")} testid="pf-code" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Chips label="Category" options={attrs.category} value={form.category} onSelect={(v) => setForm({ ...form, category: form.category === v ? "" : v })} single />
              <Chips label="Audience" options={attrs.audience} value={form.audience} onSelect={(v) => setForm({ ...form, audience: form.audience === v ? "" : v })} single />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea value={form.description} onChange={set("description")} rows={3} data-testid="pf-description" className="mt-1.5 w-full rounded-xl bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <Chips label="Colors" options={attrs.color} value={form.colors} onSelect={(v) => toggleArr("colors", v)} />
            <Chips label="Fabric" options={attrs.fabric} value={form.fabric} onSelect={(v) => setForm({ ...form, fabric: form.fabric === v ? "" : v })} single />
          </TabsContent>

          <TabsContent value="pricing" className="space-y-4 pt-4">
            <F label="MRP (₹)" type="number" value={form.mrp} onChange={set("mrp")} testid="pf-mrp" />
            <div>
              <label className="text-sm font-medium">Sizes (select, then set price & stock per size)</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {attrs.size.map((s) => (
                  <button key={s} type="button" onClick={() => toggleSize(s)} data-testid={`pf-size-${s}`}
                    className={`px-3 py-1.5 rounded-full text-sm border ${form.sizes.find((x) => x.size === s) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{s}</button>
                ))}
              </div>
              <div className="mt-3 space-y-2">
                {form.sizes.map((s) => (
                  <div key={s.size} className="grid grid-cols-3 gap-2 items-center">
                    <span className="text-sm font-medium">{s.size}</span>
                    <input type="number" placeholder="Price" value={s.price} onChange={(e) => setSizeField(s.size, "price", e.target.value)} data-testid={`pf-price-${s.size}`} className="rounded-lg bg-secondary border border-input px-2 py-1.5 text-sm" />
                    <input type="number" placeholder="Stock" value={s.stock} onChange={(e) => setSizeField(s.size, "stock", e.target.value)} data-testid={`pf-stock-${s.size}`} className="rounded-lg bg-secondary border border-input px-2 py-1.5 text-sm" />
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="media" className="space-y-4 pt-4">
            <ImageUploader label="Product Images" value={form.images} onChange={(v) => setForm({ ...form, images: v })} multiple />
            <ImageUploader label="Size Chart Image" value={form.size_chart_image} onChange={(v) => setForm({ ...form, size_chart_image: v })} />
          </TabsContent>

          <TabsContent value="specs" className="grid sm:grid-cols-2 gap-4 pt-4">
            <F label="Pattern" value={form.pattern} onChange={set("pattern")} testid="pf-pattern" />
            <F label="Net Quantity" value={form.net_quantity} onChange={set("net_quantity")} testid="pf-netqty" />
            <F label="Length" value={form.length} onChange={set("length")} />
            <F label="Neck" value={form.neck} onChange={set("neck")} />
            <F label="Sleeve Length" value={form.sleeve} onChange={set("sleeve")} />
            <F label="Padding" value={form.padding} onChange={set("padding")} />
            <F label="Add-on" value={form.addon} onChange={set("addon")} />
            <F label="Generic Name" value={form.generic_name} onChange={set("generic_name")} />
            <F label="Brand" value={form.brand} onChange={set("brand")} />
            <F label="Country of Origin" value={form.country_of_origin} onChange={set("country_of_origin")} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <label className="flex items-center gap-2 text-sm mr-auto">
            <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="pf-visible" /> Visible on store
          </label>
          <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full border border-border text-sm">Cancel</button>
          <button onClick={save} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold" data-testid="save-product-btn">Save Product</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <input type={type} value={value} onChange={onChange} data-testid={testid} className="mt-1.5 w-full rounded-xl bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary" />
  </div>
);

const Chips = ({ label, options, value, onSelect, single }) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <div className="flex flex-wrap gap-2 mt-1.5">
      {options.map((o) => {
        const active = single ? value === o : (value || []).includes(o);
        return (
          <button key={o} type="button" onClick={() => onSelect(o)}
            className={`px-3 py-1.5 rounded-full text-sm border ${active ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{o}</button>
        );
      })}
      {options.length === 0 && <span className="text-xs text-muted-foreground">Add options in Attributes tab</span>}
    </div>
  </div>
);
